import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiGet, apiPost, apiPatch, adminCompanyPath } from '../../lib/api-client';
import { useCompany } from '../../contexts/CompanyContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { CredentialDialog, type CredentialInfo } from '../../components/CredentialDialog';

interface ManagedUser {
  id: string;
  username: string;
  displayName: string;
  adminTier: 'standard' | 'manager';
  status: 'active' | 'inactive';
  assignedSellerIds: string[];
  assignedMasterMdrIds: string[];
  createdAt: string;
}

interface SellerOption {
  id: string;
  code: string;
  name: string;
}

interface MdrOption {
  id: string;
  username: string;
  displayName: string;
}

function MdrPicker({
  mdrs,
  selected,
  onToggle,
}: {
  mdrs: MdrOption[];
  selected: Set<string>;
  onToggle: (id: string) => void;
}) {
  const { t } = useTranslation();
  if (mdrs.length === 0) {
    return <p className="text-sm text-gray-400">{t('users.noMdrs')}</p>;
  }
  return (
    <div className="max-h-48 space-y-1 overflow-y-auto rounded-md border border-gray-200 p-2 dark:border-gray-700">
      {mdrs.map((m) => (
        <label
          key={m.id}
          className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300"
        >
          <input
            type="checkbox"
            checked={selected.has(m.id)}
            onChange={() => onToggle(m.id)}
            className="rounded border-gray-300"
          />
          <span className="font-mono text-xs text-gray-500">{m.username}</span>
          <span>{m.displayName}</span>
        </label>
      ))}
    </div>
  );
}

function SellerPicker({
  sellers,
  selected,
  onToggle,
}: {
  sellers: SellerOption[];
  selected: Set<string>;
  onToggle: (id: string) => void;
}) {
  const { t } = useTranslation();
  if (sellers.length === 0) {
    return <p className="text-sm text-gray-400">{t('users.noSellers')}</p>;
  }
  return (
    <div className="max-h-48 space-y-1 overflow-y-auto rounded-md border border-gray-200 p-2 dark:border-gray-700">
      {sellers.map((s) => (
        <label
          key={s.id}
          className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300"
        >
          <input
            type="checkbox"
            checked={selected.has(s.id)}
            onChange={() => onToggle(s.id)}
            className="rounded border-gray-300"
          />
          <span className="font-mono text-xs text-gray-500">{s.code}</span>
          <span>{s.name}</span>
        </label>
      ))}
    </div>
  );
}

function UserDialog({
  companyId,
  sellers,
  mdrs,
  editing,
  onClose,
  onCredential,
}: {
  companyId: string;
  sellers: SellerOption[];
  mdrs: MdrOption[];
  editing: ManagedUser | null;
  onClose: () => void;
  onCredential: (c: CredentialInfo) => void;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [form, setForm] = useState({
    username: editing?.username ?? '',
    displayName: editing?.displayName ?? '',
    adminTier: editing?.adminTier ?? ('standard' as 'standard' | 'manager'),
    password: '',
  });
  const [selected, setSelected] = useState<Set<string>>(new Set(editing?.assignedSellerIds ?? []));
  const [selectedMdrs, setSelectedMdrs] = useState<Set<string>>(
    new Set(editing?.assignedMasterMdrIds ?? []),
  );
  const [serverError, setServerError] = useState<string | null>(null);

  const toggle = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const toggleMdr = (id: string) =>
    setSelectedMdrs((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const saveMutation = useMutation({
    mutationFn: () => {
      const assignedSellerIds = [...selected];
      const assignedMasterMdrIds = [...selectedMdrs];
      if (editing) {
        return apiPatch<ManagedUser & { tempPassword?: string }>(
          adminCompanyPath(`/users/${editing.id}`, companyId),
          {
            displayName: form.displayName,
            adminTier: form.adminTier,
            assignedSellerIds,
            assignedMasterMdrIds,
          },
        );
      }
      return apiPost<ManagedUser & { tempPassword?: string }>(
        adminCompanyPath('/users', companyId),
        {
          username: form.username,
          displayName: form.displayName,
          adminTier: form.adminTier,
          password: form.password || undefined,
          assignedSellerIds,
          assignedMasterMdrIds,
        },
      );
    },
    onSuccess: (res) => {
      if (!res.ok) {
        setServerError(res.error.message);
        return;
      }
      queryClient.invalidateQueries({ queryKey: ['admin', 'managed-users'] });
      if (!editing && res.data.tempPassword) {
        onCredential({
          name: res.data.displayName,
          username: res.data.username,
          tempPassword: res.data.tempPassword,
        });
      }
      onClose();
    },
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="mx-4 w-full max-w-md rounded-lg bg-white p-6 shadow-xl dark:bg-gray-800">
        <h3 className="mb-4 text-lg font-semibold text-gray-900 dark:text-gray-100">
          {editing ? t('users.editUser') : t('users.addUser')}
        </h3>
        {serverError && (
          <div className="mb-4 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-950 dark:text-red-300">
            {serverError}
          </div>
        )}
        <form
          onSubmit={(e) => {
            e.preventDefault();
            setServerError(null);
            saveMutation.mutate();
          }}
          className="space-y-4"
        >
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
              {t('auth.username')}
            </label>
            <Input
              value={form.username}
              onChange={(e) => setForm((p) => ({ ...p, username: e.target.value }))}
              disabled={!!editing}
              required
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
              {t('users.displayName')}
            </label>
            <Input
              value={form.displayName}
              onChange={(e) => setForm((p) => ({ ...p, displayName: e.target.value }))}
              required
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
              {t('users.tier')}
            </label>
            <select
              value={form.adminTier}
              onChange={(e) =>
                setForm((p) => ({ ...p, adminTier: e.target.value as 'standard' | 'manager' }))
              }
              className="h-9 w-full rounded-md border border-gray-200 bg-white px-2 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200"
            >
              <option value="standard">{t('adminTier.standard')}</option>
              <option value="manager">{t('adminTier.manager')}</option>
            </select>
            <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">
              {form.adminTier === 'standard' ? t('users.standardHint') : t('users.managerHint')}
            </p>
          </div>
          {!editing && (
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                {t('users.passwordOptional')}
              </label>
              <Input
                type="text"
                value={form.password}
                onChange={(e) => setForm((p) => ({ ...p, password: e.target.value }))}
                placeholder={t('users.passwordPlaceholder')}
              />
            </div>
          )}
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
              {t('users.assignedSellers')}
            </label>
            <SellerPicker sellers={sellers} selected={selected} onToggle={toggle} />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
              {t('users.assignedMdrs')}
            </label>
            <MdrPicker mdrs={mdrs} selected={selectedMdrs} onToggle={toggleMdr} />
          </div>
          <div className="flex gap-2 pt-2">
            <Button type="submit" disabled={saveMutation.isPending} className="flex-1">
              {saveMutation.isPending ? t('common.loading') : t('common.save')}
            </Button>
            <Button type="button" variant="ghost" onClick={onClose}>
              {t('common.cancel')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

export function AdminUsers() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { activeCompanyId } = useCompany();
  const [dialog, setDialog] = useState<{ editing: ManagedUser | null } | null>(null);
  const [credential, setCredential] = useState<CredentialInfo | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'managed-users', activeCompanyId],
    queryFn: () =>
      apiGet<{ items: ManagedUser[]; total: number }>(adminCompanyPath('/users', activeCompanyId!)),
    enabled: !!activeCompanyId,
  });

  const { data: sellerData } = useQuery({
    queryKey: ['admin', 'sellers', activeCompanyId],
    queryFn: () =>
      apiGet<{ items: SellerOption[] }>(adminCompanyPath('/sellers', activeCompanyId!)),
    enabled: !!activeCompanyId,
  });

  const { data: mdrData } = useQuery({
    queryKey: ['admin', 'mdr-masters', activeCompanyId],
    queryFn: () => apiGet<{ items: MdrOption[] }>(adminCompanyPath('/mdr', activeCompanyId!)),
    enabled: !!activeCompanyId,
  });

  const statusMutation = useMutation({
    mutationFn: ({ id, status }: { id: string; status: 'active' | 'inactive' }) =>
      apiPatch(adminCompanyPath(`/users/${id}`, activeCompanyId!), { status }),
    onSuccess: (res) => {
      if (res.ok) queryClient.invalidateQueries({ queryKey: ['admin', 'managed-users'] });
      else setActionError(res.error.message);
    },
  });

  const resetMutation = useMutation({
    mutationFn: (u: ManagedUser) =>
      apiPost<{ username: string; tempPassword: string }>(
        adminCompanyPath(`/users/${u.id}/reset-password`, activeCompanyId!),
      ),
    onSuccess: (res, u) => {
      if (res.ok) {
        setCredential({
          name: u.displayName,
          username: res.data.username,
          tempPassword: res.data.tempPassword,
        });
      } else {
        setActionError(res.error.message);
      }
    },
  });

  const users = data?.ok ? data.data.items : [];
  const sellers = sellerData?.ok ? sellerData.data.items : [];
  const mdrs = mdrData?.ok ? mdrData.data.items : [];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-end">
        <Button onClick={() => setDialog({ editing: null })} disabled={!activeCompanyId}>
          {t('users.addUser')}
        </Button>
      </div>

      {!activeCompanyId && (
        <div className="rounded-md border border-amber-200 bg-amber-50 p-4 text-amber-800 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-200">
          {t('company.noCompanySelected')}
        </div>
      )}

      {actionError && (
        <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-950 dark:text-red-300">
          {actionError}
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle>{t('users.title')}</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading && <div className="p-6 text-gray-400">{t('common.loading')}</div>}
          {error && <div className="p-6 text-red-600">{t('common.error')}</div>}
          {!isLoading && users.length === 0 && (
            <div className="p-6 text-gray-400">{t('common.noData')}</div>
          )}
          {users.length > 0 && (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('auth.username')}</TableHead>
                  <TableHead>{t('users.displayName')}</TableHead>
                  <TableHead>{t('users.tier')}</TableHead>
                  <TableHead>{t('sellers.status')}</TableHead>
                  <TableHead>{t('users.assignedCount')}</TableHead>
                  <TableHead>{t('sellers.actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((u) => (
                  <TableRow key={u.id}>
                    <TableCell className="font-mono text-sm">{u.username}</TableCell>
                    <TableCell>{u.displayName}</TableCell>
                    <TableCell>
                      <Badge variant={u.adminTier === 'standard' ? 'default' : 'warning'}>
                        {t(`adminTier.${u.adminTier}`)}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant={u.status === 'active' ? 'success' : 'danger'}>
                        {t(`common.${u.status}`)}
                      </Badge>
                    </TableCell>
                    <TableCell>{u.assignedSellerIds.length}</TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-2">
                        <Button variant="ghost" size="sm" onClick={() => setDialog({ editing: u })}>
                          {t('common.edit')}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            if (
                              window.confirm(t('sellers.confirmReset', { name: u.displayName }))
                            ) {
                              resetMutation.mutate(u);
                            }
                          }}
                          disabled={resetMutation.isPending}
                        >
                          {t('sellers.resetPassword')}
                        </Button>
                        {u.status === 'active' ? (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-600 dark:text-red-400"
                            onClick={() => {
                              if (window.confirm(t('common.confirm'))) {
                                statusMutation.mutate({ id: u.id, status: 'inactive' });
                              }
                            }}
                            disabled={statusMutation.isPending}
                          >
                            {t('common.deactivate')}
                          </Button>
                        ) : (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-green-600 dark:text-green-400"
                            onClick={() => statusMutation.mutate({ id: u.id, status: 'active' })}
                            disabled={statusMutation.isPending}
                          >
                            {t('common.activate')}
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {dialog && activeCompanyId && (
        <UserDialog
          companyId={activeCompanyId}
          sellers={sellers}
          mdrs={mdrs}
          editing={dialog.editing}
          onClose={() => setDialog(null)}
          onCredential={setCredential}
        />
      )}

      {credential && (
        <CredentialDialog credential={credential} onClose={() => setCredential(null)} />
      )}
    </div>
  );
}
